import { Web3dTool } from '@trimble/web3d-providers';
import { PolylineMeasurementTool } from '@technology/web3d-plugin-markup';
import { Accessor, createEffect, createSignal, on, Setter } from 'solid-js';
import { SimpleMeasurementCapabilityBase } from './base-simple-measurement-capability';
import {
  PolylineMeasurementSettings,
  MeasurementsCapabilityProvider,
  SlopeFormat,
} from '@trimble/web3d-providers';
import { detectArrayChanges, SlopeFormatter } from '@trimble/utilities';
import { Web3DViewerWrapper } from '@trimble/web3d-utils';
import { Web3DViewer } from '@technology/web3d';

export class PolylineMeasurementCapability extends SimpleMeasurementCapabilityBase {
  protected measurementType: Web3dTool = Web3dTool.PolylineMeasurement;

  public readonly polylineMeasurementSettings: Accessor<PolylineMeasurementSettings>;
  public readonly setPolylineMeasurementSettings: Setter<PolylineMeasurementSettings>;
  public readonly slopeFormat: Accessor<SlopeFormat>;
  public readonly setSlopeFormat: Setter<SlopeFormat>;

  constructor(measurementsProvider: MeasurementsCapabilityProvider) {
    super(measurementsProvider);

    const [polylineMeasurementSettings, setPolylineMeasurementSettings] =
      createSignal<PolylineMeasurementSettings>({
        showSegmentDistances: false,
        showSegmentSlopes: false,
        showHorizontalMeasurements: false,
        showVerticalMeasurements: false,
      });

    const [slopeFormat, setSlopeFormat] = createSignal<SlopeFormat>('ratio');

    this.polylineMeasurementSettings = polylineMeasurementSettings;
    this.setPolylineMeasurementSettings = setPolylineMeasurementSettings;
    this.slopeFormat = slopeFormat;
    this.setSlopeFormat = setSlopeFormat;

    // Helper function to apply settings to specific viewers
    const applySettingsToViewers = (
      viewers: Web3DViewerWrapper[],
      settings: PolylineMeasurementSettings
    ): void => {
      for (const viewer of viewers) {
        applySettingsToViewer(viewer.viewer, settings);
      }
    };

    // Helper function to apply settings to a single viewer
    const applySettingsToViewer = (
      viewer: Web3DViewer,
      settings: PolylineMeasurementSettings
    ): void => {
      // Apply settings to the tool
      const polylineMeasurementTool = viewer.tools.get(PolylineMeasurementTool);

      if (polylineMeasurementTool) {
        polylineMeasurementTool.showSegmentDistances =
          settings.showSegmentDistances;
        polylineMeasurementTool.showSegmentSlopes = settings.showSegmentSlopes;
        polylineMeasurementTool.showHorizontalMeasurements =
          settings.showHorizontalMeasurements;
        polylineMeasurementTool.showVerticalMeasurements =
          settings.showVerticalMeasurements;
      }
    };

    // Helper function to apply slope format to specific viewers
    const applySlopeFormatToViewers = (
      viewerWrappers: Web3DViewerWrapper[],
      slopeFormat: SlopeFormat
    ): void => {
      for (const viewerWrapper of viewerWrappers) {
        viewerWrapper.viewer.settings.customSlopeAngleFormatter = (
          vertical: number,
          horizontal: number | undefined
        ) => {
          const horizontalValue = horizontal ?? 0;
          const slopeRatio =
            horizontalValue !== 0 ? vertical / horizontalValue : 0;

          return SlopeFormatter.toDisplay({
            measuredValue: slopeRatio,
            currentUnits: 'ratio',
            settings: { format: slopeFormat },
          });
        };
      }
    };

    // Sync polyline measurement settings to viewers
    createEffect(
      on(
        [this.polylineMeasurementSettings, this.viewerWrappers],
        ([settings, viewers], prevValues) => {
          const prevViewers = prevValues?.[1];
          const { newItems } = detectArrayChanges(prevViewers, viewers);

          // If new viewers are available, apply settings only to new viewers
          if (newItems.length > 0) {
            applySettingsToViewers(newItems, settings);
          } else {
            // Otherwise, apply settings to all viewers
            applySettingsToViewers(viewers, settings);
          }
        }
      )
    );

    // Sync slope format to viewers
    createEffect(
      on(
        [this.slopeFormat, this.viewerWrappers],
        ([slopeFormat, viewers], prevValues) => {
          const prevViewers = prevValues?.[1];
          const { newItems } = detectArrayChanges(prevViewers, viewers);

          // If new viewers are available, apply slope format only to new viewers
          if (newItems.length > 0) {
            applySlopeFormatToViewers(newItems, slopeFormat);
          } else {
            // Otherwise, apply slope format to all viewers
            applySlopeFormatToViewers(viewers, slopeFormat);
          }
        }
      )
    );
  }
}
